import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import './design-system/animations.css';
import './index.css';
import App from './App';
import { AppErrorBoundary } from './components/common/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { applyNativeDocumentClasses } from './native/platform';
import { perfMarkStart, perfAttachWebVitals, perfLogSummary, perfTimeline } from './utils/startupPerf';
import { initInstallPrompt } from './pwa/installPrompt';
import { markBootSplashShown } from './pwa/bootSplash';
import { initBackgroundSyncListeners } from './pwa/backgroundSync';
import { registerServiceWorker } from './pwa/serviceWorkerRegistration';
import { initTicketDeepLinkRecovery, shouldSkipServiceWorkerControllerReload, logTicketIndexBoot, isTicketDeepLinkPath } from './pwa/ticketDeepLinkRecovery';
import { isPublicSiteRoute } from './routes/appRoutes';
import { logBuildVersion } from './config/buildInfo';

logBuildVersion();
logTicketIndexBoot();

perfTimeline('app.scriptStart');
perfMarkStart('js.bundle');
perfMarkStart('pwa.timeToInteractive');
perfMarkStart('pwa.firstMeaningfulPaint');
markBootSplashShown();
initTicketDeepLinkRecovery();
initInstallPrompt();
applyNativeDocumentClasses();

/** Hash routing in native WebViews avoids deep-link 404s before store release. */
const AppRouter = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

// Force favicon to load (fixes dev server and cache)
(function setFavicon() {
  const publicUrl = process.env.PUBLIC_URL || '';
  const href = `${publicUrl}/favicon.ico?v=3`;
  let link = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'shortcut icon';
    document.head.appendChild(link);
  }
  link.href = href;
})();

if (typeof window !== 'undefined' && isTicketDeepLinkPath(window.location.pathname)) {
  console.log('[ticket] React render called');
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AppRouter>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </AppRouter>
  </React.StrictMode>
);

reportWebVitals(perfAttachWebVitals);

if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => perfLogSummary(), 3000);
  });

  if ('serviceWorker' in navigator && !Capacitor.isNativePlatform()) {
    window.addEventListener('load', () => {
      const onTicketRoute = isTicketDeepLinkPath(window.location.pathname);
      let pendingTicket = false;
      try {
        pendingTicket = Boolean(
          sessionStorage.getItem('lockedin_pending_ticket_code')
          || localStorage.getItem('lockedin_pending_ticket_ls')
        );
      } catch (_) {}

      if (onTicketRoute || pendingTicket) {
        console.log('[ticket-sw] registration skipped on ticket route', {
          onTicketRoute,
          pendingTicket,
        });
        return;
      }

      // Keep the PWA service worker scoped to the app, not the public
      // marketing site. We skip registration on public routes so the website
      // stays a plain, cache-light site.
      if (isPublicSiteRoute(window.location.pathname)) {
        console.log('[pwa-sw] registration skipped on public marketing route', {
          path: window.location.pathname,
        });
        return;
      }

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_ACTIVATED') {
          console.log('[pwa-sw] active build', event.data.build, event.data.cache);
        }
      });

      registerServiceWorker().then((registration) => {
        if (registration && process.env.NODE_ENV === 'production') {
          let reloaded = false;
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloaded) return;
            if (shouldSkipServiceWorkerControllerReload()) return;
            reloaded = true;
            window.location.reload();
          });
        }
      });
      if (process.env.NODE_ENV === 'production') {
        initBackgroundSyncListeners();
      }
    });
  }
}
